import * as React from "react";
import type { EditorRef } from "react-email-editor";
import { Code2, Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, cn } from "@eduviaos/ui-kit";
import { MonacoEditor } from "@/components/email-templates/MonacoEditor";
import type { TemplateVariable } from "@/lib/emailTemplates/types";

// `react-email-editor` is browser-only and ships with web-worker assets;
// we lazy-load it so the dashboard bundle stays under budget. The editor
// chunk only ships when an SA opens a template editor route.
const ReactEmailEditor = React.lazy(() =>
  import("react-email-editor").then((m) => ({ default: m.default })),
);

type EditorMode = "visual" | "code";

/** Narrow surface of the Unlayer editor instance we use. The lib's
 * exported `UnlayerEditor` type has 65+ properties; we only need
 * three. We coerce via the cast below in `flushVisualChange`. */
interface UnlayerSurface {
  loadDesign: (design: object) => void;
  exportHtml: (
    cb: (data: { html: string; design: object }) => void,
    opts?: { cleanup?: boolean },
  ) => void;
  addEventListener?: (event: string, handler: () => void) => void;
}

export interface EmailEditorValue {
  /** Compiled HTML the BE persists + sends. Always present. */
  html_body: string;
  /** Plaintext alternative. Required by the BE; derived from HTML when
   * the SA hasn't typed one explicitly in Code mode. */
  text_body: string;
  /** Unlayer round-trip payload. Null when the SA worked in Code mode
   * (hand-edited HTML) so the BE clears the previous design. */
  design_json: Record<string, unknown> | null;
}

export interface EmailEditorProps {
  /** Initial buffer hydrated from the BE. */
  initial: EmailEditorValue;
  /** Called whenever the SA changes anything. Debouncing is internal. */
  onChange: (next: EmailEditorValue) => void;
  /** Catalogue variables — exposed in Unlayer's "Insert merge tag" sidebar. */
  variables: readonly TemplateVariable[];
  /** Tenant primary colour from `Tenant.branding`, used as the Unlayer
   * "appearance.theme" accent. Falls back to platform indigo. */
  brandPrimary?: string | null;
  /** When true, default to Code mode (legacy template with no design_json). */
  legacy?: boolean;
}

/**
 * Strip HTML to a plain-text alternative.
 *
 * Not perfect — but good enough for the deliverability requirement
 * that every multipart email ship a text/plain body. Marketing
 * templates with rich layouts are unlikely to need a hand-tuned text
 * version; transactional templates fit this stripping just fine.
 */
function stripHtml(html: string): string {
  if (typeof window === "undefined") return html;
  const div = document.createElement("div");
  div.innerHTML = html;
  // Convert <br> and block-level closes into newlines so the result
  // doesn't run together.
  div.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  div.querySelectorAll("p, div, li, h1, h2, h3, h4, h5, h6, tr").forEach((el) => {
    el.append("\n");
  });
  return (div.textContent ?? "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Translate our catalogue variables into Unlayer's mergeTags shape.
 * Picking up `{{ variable }}` requires Jinja-style spaces inside the
 * braces; the BE's renderer accepts both `{{var}}` and `{{ var }}`.
 */
function buildMergeTags(
  variables: readonly TemplateVariable[],
): Record<string, { name: string; value: string; sample?: string }> {
  const out: Record<string, { name: string; value: string; sample?: string }> = {};
  for (const v of variables) {
    out[v.name] = {
      name: v.label,
      value: `{{ ${v.name} }}`,
      sample: v.sample_value,
    };
  }
  return out;
}

export const EmailEditor = React.forwardRef<
  { exportNow: () => Promise<EmailEditorValue> },
  EmailEditorProps
>(function EmailEditor({ initial, onChange, variables, brandPrimary, legacy }, ref) {
  const [mode, setMode] = React.useState<EditorMode>(legacy ? "code" : "visual");
  const [htmlBody, setHtmlBody] = React.useState(initial.html_body);
  const [textBody, setTextBody] = React.useState(initial.text_body);
  const [designJson, setDesignJson] = React.useState(initial.design_json);
  const [editorReady, setEditorReady] = React.useState(false);
  const [editorError, setEditorError] = React.useState<string | null>(null);
  const [showSwitchWarning, setShowSwitchWarning] = React.useState(false);

  // Imperative handle to the underlying Unlayer instance. We use the
  // lib's exported `EditorRef` type so the JSX prop types line up;
  // when we touch `editor` in callbacks we cast to our narrow
  // `UnlayerSurface` shape.
  const editorRef = React.useRef<EditorRef | null>(null);

  // Hydrate initial design + subscribe to change events once the
  // editor canvas finishes booting. We do this here (not via a
  // dedicated React prop) because the typed `onDesignUpdated` prop
  // doesn't exist in `react-email-editor` — the lib instead exposes
  // an imperative `addEventListener('design:updated', …)` API.
  const onEditorReady = React.useCallback(
    (rawUnlayer: unknown) => {
      const unlayer = rawUnlayer as UnlayerSurface;
      setEditorReady(true);
      if (designJson) {
        try {
          unlayer.loadDesign(designJson as object);
        } catch (e) {
          setEditorError(`Could not load saved design: ${(e as Error).message ?? String(e)}`);
        }
      }
      // Subscribe to design changes. Each fires → we re-export and
      // bubble fresh values to the parent.
      unlayer.addEventListener?.("design:updated", () => {
        flushVisualChange();
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [designJson],
  );

  // Whenever the user makes a change in Unlayer, we extract HTML +
  // design + a derived plaintext, and bubble up via onChange. We
  // debounce with `requestIdleCallback` so a frantic edit session
  // doesn't spam the parent.
  const flushVisualChange = React.useCallback(() => {
    const unlayer = editorRef.current?.editor as UnlayerSurface | undefined;
    if (!unlayer) return;
    unlayer.exportHtml(
      (data) => {
        const compiledHtml = data.html;
        const compiledDesign = data.design;
        const derivedText = stripHtml(compiledHtml) || "Open this email in HTML view.";
        setHtmlBody(compiledHtml);
        setTextBody(derivedText);
        setDesignJson(compiledDesign as Record<string, unknown>);
        onChange({
          html_body: compiledHtml,
          text_body: derivedText,
          design_json: compiledDesign as Record<string, unknown>,
        });
      },
      { cleanup: false },
    );
  }, [onChange]);

  // Code mode: the SA edits HTML directly. We clear design_json on
  // first keystroke so the BE doesn't keep a stale visual structure.
  const onHtmlEdit = (next: string) => {
    setHtmlBody(next);
    if (designJson !== null) setDesignJson(null);
    onChange({ html_body: next, text_body: textBody, design_json: null });
  };
  const onTextEdit = (next: string) => {
    setTextBody(next);
    onChange({ html_body: htmlBody, text_body: next, design_json: designJson });
  };

  // Expose an imperative export so the parent's Save button can pull
  // the freshest values out of Unlayer (debounce gap notwithstanding).
  React.useImperativeHandle(
    ref,
    () => ({
      async exportNow() {
        if (mode === "code") {
          return { html_body: htmlBody, text_body: textBody, design_json: designJson };
        }
        const unlayer = editorRef.current?.editor as UnlayerSurface | undefined;
        if (!unlayer) {
          return { html_body: htmlBody, text_body: textBody, design_json: designJson };
        }
        return new Promise((resolve) => {
          unlayer.exportHtml((data) => {
            const compiledHtml = data.html;
            const compiledDesign = data.design;
            const derivedText = stripHtml(compiledHtml) || "Open this email in HTML view.";
            resolve({
              html_body: compiledHtml,
              text_body: derivedText,
              design_json: compiledDesign as Record<string, unknown>,
            });
          });
        });
      },
    }),
    [mode, htmlBody, textBody, designJson],
  );

  const onModeChange = (next: string) => {
    const nextMode = next as EditorMode;
    if (nextMode === mode) return;
    if (nextMode === "visual" && designJson === null && htmlBody.length > 0) {
      // Switching to Visual will discard the SA's hand-edited HTML
      // because Unlayer can only render from a design JSON. Surface
      // the warning and require an explicit confirm.
      setShowSwitchWarning(true);
      return;
    }
    setMode(nextMode);
  };

  const confirmSwitchToVisual = () => {
    setShowSwitchWarning(false);
    setMode("visual");
  };

  const mergeTags = React.useMemo(() => buildMergeTags(variables), [variables]);

  const editorOptions = React.useMemo(
    () => ({
      mergeTags,
      appearance: {
        theme: "modern_light",
        panels: {
          tools: { dock: "left" },
        },
        loader: { html: "" },
      },
      features: {
        textEditor: { spellChecker: true },
        preview: true,
        smartMergeTags: true,
        undoRedo: true,
      },
      designTags: {
        brand_primary: brandPrimary ?? "#4f46e5",
      },
      // Suppress the Unlayer "Powered by" footer. Their MIT embed
      // allows hiding the badge if you provide attribution elsewhere
      // (we'll add one in the help/about page).
      tools: {
        // Reserved for future custom blocks.
      },
    }),
    [mergeTags, brandPrimary],
  );

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between gap-3">
        <Tabs value={mode} onValueChange={onModeChange}>
          <TabsList>
            <TabsTrigger value="visual">
              <Sparkles className="size-3.5" aria-hidden="true" />
              Visual
            </TabsTrigger>
            <TabsTrigger value="code">
              <Code2 className="size-3.5" aria-hidden="true" />
              Code
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <p className="text-muted-foreground text-xs">
          {mode === "visual"
            ? "Drag-drop blocks. Saves restore the design on next edit."
            : "Hand-edit raw HTML / plaintext. Saving here discards the visual design."}
        </p>
      </div>

      {showSwitchWarning && (
        <div
          role="alertdialog"
          className="flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2.5 text-sm"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" aria-hidden="true" />
          <div className="grid gap-2">
            <p>
              <strong>Switching to Visual will start fresh.</strong> The HTML you&apos;ve
              hand-edited can&apos;t be re-imported into the drag-drop builder. You can either keep
              editing in Code mode, or discard the current HTML and start a new visual design.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={confirmSwitchToVisual}
                className="rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700"
              >
                Discard HTML &amp; switch
              </button>
              <button
                type="button"
                onClick={() => setShowSwitchWarning(false)}
                className="bg-card rounded-md border px-2.5 py-1 text-xs"
              >
                Stay in Code
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={cn(
          "bg-card rounded-md border",
          mode === "visual" ? "min-h-[640px] overflow-hidden" : "p-3",
        )}
      >
        {mode === "visual" ? (
          <React.Suspense
            fallback={
              <div className="text-muted-foreground grid h-[640px] place-items-center text-sm">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  Loading editor…
                </span>
              </div>
            }
          >
            {!editorReady && (
              <div className="bg-card/60 text-muted-foreground absolute z-10 grid h-[640px] w-full place-items-center text-sm">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  Loading editor…
                </span>
              </div>
            )}
            <ReactEmailEditor
              ref={editorRef}
              minHeight={640}
              onReady={onEditorReady}
              options={editorOptions as never}
            />
          </React.Suspense>
        ) : (
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                HTML body
              </p>
              <MonacoEditor
                language="html"
                height="320px"
                value={htmlBody}
                onChange={onHtmlEdit}
                ariaLabel="HTML body editor"
              />
            </div>
            <div className="grid gap-1.5">
              <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                Plain-text alternative
              </p>
              <MonacoEditor
                language="plaintext"
                height="180px"
                value={textBody}
                onChange={onTextEdit}
                ariaLabel="Plain text body editor"
              />
            </div>
          </div>
        )}
      </div>

      {editorError && (
        <p role="alert" className="text-destructive text-xs">
          {editorError}
        </p>
      )}
    </div>
  );
});
